import { useState, useCallback, useMemo } from "react";
import {
  IStudyPersonnelProtocol,
  IAddEditStudySite,
  IAddEditStudySiteProtocol
} from "../model";
import { sortProtocolsForEditMode } from "../components/BktStudy/MddAddEditStudySite/shared/studySiteUtils";

/**
 * Custom hook to manage protocol-related state and operations for a study site
 * Handles adding, deleting, and validating protocols
 *
 * @param studySite - The current study site object
 * @param setStudySite - Function to update the study site state
 * @param isEditMode - Whether the component is in edit mode (affects sorting and behavior)
 * @returns Protocol management state and handlers
 */
export const useStudySiteProtocols = (
  studySite: IAddEditStudySite,
  setStudySite: (studySite: IAddEditStudySite) => void,
  isEditMode: boolean = false
) => {
  // Track if user has manually added protocols (vs initial load)
  const [protocolsAddedByUser, setProtocolsAddedByUser] = useState(false);

  // Track validation errors for individual protocols
  const [protocolValidationErrors, setProtocolValidationErrors] = useState<
    Record<string, boolean>
  >({});

  // Track delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    protocolId: string;
    protocolName: string;
  }>({
    isOpen: false,
    protocolId: "",
    protocolName: ""
  });

  /**
   * Add new protocols to the study site
   * New protocols inherit the main sponsor site ID
   * In edit mode: new protocols go to top, existing stay at bottom
   * In add mode: all protocols sorted alphabetically by name
   */
  const handleAddProtocols = useCallback(
    (newProtocols: IStudyPersonnelProtocol[]) => {
      const existingProtocols = studySite.protocols || [];

      // Initialize new protocols with the main sponsor site ID, mark as not existing, and set as active
      const protocolsWithSponsorId: IAddEditStudySiteProtocol[] = newProtocols.map(
        protocol => ({
          ...protocol,
          sponsorSiteId: studySite.sponsorSiteId, // Always inherit from main sponsor site ID
          isExisting: false,
          isActive: true // Newly added protocols should be active by default
        })
      );

      const updatedProtocols = [
        ...existingProtocols,
        ...protocolsWithSponsorId
      ];

      // Use different sorting based on mode
      const sortedProtocols = isEditMode
        ? sortProtocolsForEditMode(updatedProtocols)
        : updatedProtocols.sort((a, b) =>
            a.protocolName.localeCompare(b.protocolName)
          );

      setStudySite({
        ...studySite,
        protocols: sortedProtocols
      });
      setProtocolsAddedByUser(true);
    },
    [studySite, setStudySite, isEditMode]
  );

  /**
   * Show delete confirmation dialog for a protocol
   */
  const handleProtocolDeleteRequest = useCallback(
    (protocol: IStudyPersonnelProtocol) => {
      setDeleteConfirmation({
        isOpen: true,
        protocolId: protocol.id,
        protocolName: protocol.protocolName
      });
    },
    []
  );

  /**
   * Confirm and delete a protocol from the study site
   * Also cleans up any validation errors for the deleted protocol
   * Resets protocolsAddedByUser to false if all protocols are deleted
   */
  const handleProtocolDeleteConfirm = useCallback(() => {
    const { protocolId } = deleteConfirmation;
    if (!protocolId) {
      // Close confirmation dialog if no protocol to delete
      setDeleteConfirmation({
        isOpen: false,
        protocolId: "",
        protocolName: ""
      });
      return;
    }

    const protocols = studySite.protocols || [];
    const updatedProtocols = protocols.filter(p => p.id !== protocolId);

    setStudySite({
      ...studySite,
      protocols: updatedProtocols
    });

    // Clean up validation errors for deleted protocol
    setProtocolValidationErrors(prev => {
      const { [protocolId]: _, ...rest } = prev;
      return rest;
    });

    // Reset protocolsAddedByUser to false if all protocols are deleted
    if (updatedProtocols.length === 0) {
      setProtocolsAddedByUser(false);
    }

    // Close confirmation dialog
    setDeleteConfirmation({
      isOpen: false,
      protocolId: "",
      protocolName: ""
    });
  }, [deleteConfirmation, studySite, setStudySite]);

  /**
   * Cancel delete confirmation
   */
  const handleProtocolDeleteCancel = useCallback(() => {
    setDeleteConfirmation({
      isOpen: false,
      protocolId: "",
      protocolName: ""
    });
  }, []);

  /**
   * Track validation errors for individual protocols
   * Used to disable save button when there are validation errors
   */
  const handleProtocolValidationError = useCallback(
    (protocolId: string, hasError: boolean) => {
      setProtocolValidationErrors(prev => ({
        ...prev,
        [protocolId]: hasError
      }));
    },
    []
  );

  /**
   * Check if any protocols have validation errors
   * Memoized for performance
   */
  const hasProtocolValidationErrors = useMemo(
    () => Object.values(protocolValidationErrors).some(hasError => hasError),
    [protocolValidationErrors]
  );

  return {
    // State
    protocolsAddedByUser,
    protocolValidationErrors,
    hasProtocolValidationErrors,
    deleteConfirmation,

    // Actions
    handleAddProtocols,
    handleProtocolDeleteRequest,
    handleProtocolDeleteConfirm,
    handleProtocolDeleteCancel,
    handleProtocolValidationError
  };
};
